import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Banknote,
  Building2,
  FileText,
  LayoutDashboard,
  Landmark,
  Map,
  Receipt,
  Users,
  UserCog,
  BarChart3,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { can, type Permission } from '@/domain/permissions';
import { useAuthStore } from '@/app/store/authStore';

interface NavItem {
  to: string;
  labelKey: string;
  icon: JSX.Element;
  permission: Permission;
  /** Agents get a different primary action, so some items are role-specific. */
  agentOnly?: boolean;
  hideForAgent?: boolean;
}

const ITEMS: NavItem[] = [
  {
    to: '/',
    labelKey: 'nav.dashboard',
    icon: <LayoutDashboard className="h-4 w-4" />,
    permission: 'dashboard:read',
  },
  {
    to: '/collect',
    labelKey: 'nav.collect',
    icon: <Banknote className="h-4 w-4" />,
    permission: 'collections:create',
    agentOnly: true,
  },
  {
    to: '/collections',
    labelKey: 'nav.collections',
    icon: <Receipt className="h-4 w-4" />,
    permission: 'collections:read',
  },
  {
    to: '/invoices',
    labelKey: 'nav.invoices',
    icon: <FileText className="h-4 w-4" />,
    permission: 'invoices:read',
  },
  {
    to: '/reports',
    labelKey: 'nav.reports',
    icon: <BarChart3 className="h-4 w-4" />,
    permission: 'reports:read',
  },
  {
    to: '/shops',
    labelKey: 'nav.shops',
    icon: <Building2 className="h-4 w-4" />,
    permission: 'shops:read',
    hideForAgent: true,
  },
  {
    to: '/agents',
    labelKey: 'nav.agents',
    icon: <Users className="h-4 w-4" />,
    permission: 'agents:read',
    hideForAgent: true,
  },
  {
    to: '/routes',
    labelKey: 'nav.routes',
    icon: <Map className="h-4 w-4" />,
    permission: 'routes:read',
    hideForAgent: true,
  },
  {
    to: '/banks',
    labelKey: 'nav.banks',
    icon: <Landmark className="h-4 w-4" />,
    permission: 'banks:create',
    hideForAgent: true,
  },
  {
    to: '/users',
    labelKey: 'nav.users',
    icon: <UserCog className="h-4 w-4" />,
    permission: 'users:read',
    hideForAgent: true,
  },
  {
    to: '/audit-log',
    labelKey: 'nav.auditLog',
    icon: <ShieldCheck className="h-4 w-4" />,
    permission: 'audit:read',
    hideForAgent: true,
  },
];

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const isAgent = user?.role === 'AGENT';

  const visible = ITEMS.filter((item) => {
    if (!can(user, item.permission)) return false;
    if (item.agentOnly && !isAgent) return false;
    if (item.hideForAgent && isAgent) return false;
    return true;
  });

  return (
    <nav className="flex flex-col gap-0.5 p-3">
      {visible.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-50 text-brand-800'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )
          }
        >
          {item.icon}
          {t(item.labelKey as never)}
        </NavLink>
      ))}
    </nav>
  );
}
